<script lang="ts">
  import Modal from '../common/Modal.svelte';
  import Button from '../common/Button.svelte';
  import ProgressBar from '../common/ProgressBar.svelte';
  import CategorySetupStep from './CategorySetupStep.svelte';
  import { markWizardCompleted, markWizardSkipped } from '../../lib/onboardingState';

  type Props = {
    onComplete: () => void;
  };

  let { onComplete }: Props = $props();

  let isOpen = $state(true);
  let currentStep = $state(0);

  const steps = [
    {
      title: 'Upload Your Data',
      icon: 'upload',
      type: 'info',
      content: {
        heading: 'Import your bank transactions',
        description:
          "To get started, you'll need to upload CSV files from your bank. Most banks allow you to export transaction history in CSV format.",
        tips: [
          'Go to Settings → Data Management tab',
          'Drag and drop your CSV files or click to browse',
          'The app will automatically detect your bank format',
          'You can upload multiple files at once',
        ],
        illustration: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12',
      },
    },
    {
      title: 'Customize Categories',
      icon: 'categories',
      type: 'interactive',
      content: {
        heading: 'Review and customize your categories',
        description:
          'Before importing transactions, you can customize the default categories to match your preferences.',
      },
    },
    {
      title: 'Categorization Explained',
      icon: 'categories',
      type: 'info',
      content: {
        heading: 'How your transactions are categorized',
        description:
          'Keep Finance uses a smart 3-tier system to automatically categorize your transactions, saving you time and effort.',
        tips: [
          '1. Keyword Matching: Common merchants are instantly recognized',
          '2. Rules: Your custom patterns are applied automatically',
          '3. AI (Optional): Advanced AI can categorize complex transactions',
          'You can always edit categories or create rules for better accuracy',
        ],
        illustration: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
      },
    },
    {
      title: 'Explore Your Dashboard',
      icon: 'dashboard',
      type: 'info',
      content: {
        heading: 'Your financial overview',
        description:
          'The Overview tab is your command center. See your income, expenses, and transaction history at a glance.',
        tips: [
          'View monthly income and expense summaries',
          'Filter transactions by date, category, person, or amount',
          'Click any transaction to edit it inline',
          'Use bulk actions to update multiple transactions at once',
        ],
        illustration: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      },
    },
    {
      title: 'Next Steps',
      icon: 'next',
      type: 'info',
      content: {
        heading: "You're all set!",
        description:
          'You now know the basics. Here are some powerful features to explore when you\'re ready:',
        tips: [
          '📋 Rules: Create patterns to auto-categorize recurring merchants',
          '💰 Budgets: Set spending limits and track your progress',
          '🔁 Recurring: Automatically detect subscriptions and bills',
          '📊 Visualizations: View spending trends with charts and graphs',
        ],
        illustration: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      },
    },
  ];

  const totalSteps = steps.length;

  function handleNext() {
    if (currentStep < totalSteps - 1) {
      currentStep++;
    } else {
      handleComplete();
    }
  }

  function handleBack() {
    if (currentStep > 0) {
      currentStep--;
    }
  }

  function handleSkip() {
    markWizardSkipped();
    isOpen = false;
    onComplete();
  }

  function handleComplete() {
    markWizardCompleted();
    isOpen = false;
    onComplete();
  }

  const progress = $derived((currentStep + 1) / totalSteps * 100);
  const currentStepData = $derived(steps[currentStep]);
  const isLastStep = $derived(currentStep === totalSteps - 1);
</script>

<Modal bind:isOpen={isOpen} size="md" showCloseButton={false}>
  <div class="wizard-container">
    <div class="wizard-header">
      <div class="progress-section">
        <div class="step-indicator">
          Step {currentStep + 1} of {totalSteps}
        </div>
        <ProgressBar value={progress} showPercentage={false} />
      </div>

      <button class="skip-button" onclick={handleSkip} type="button">Skip Tour</button>
    </div>

    <div class="wizard-content">
      {#if currentStepData.type === 'info'}
        <div class="illustration-container">
          <svg class="illustration-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d={currentStepData.content.illustration}
            ></path>
          </svg>
        </div>

        <h2 class="step-title">{currentStepData.title}</h2>
        <h3 class="step-heading">{currentStepData.content.heading}</h3>
        <p class="step-description">{currentStepData.content.description}</p>

        <ul class="tips-list">
          {#each currentStepData.content.tips as tip}
            <li class="tip-item">{tip}</li>
          {/each}
        </ul>
      {:else if currentStepData.type === 'interactive'}
        <h2 class="step-title">{currentStepData.title}</h2>
        <p class="step-description">{currentStepData.content.description}</p>
        <CategorySetupStep />
      {/if}
    </div>

    <div class="wizard-actions">
      <Button
        variant="secondary"
        onclick={handleBack}
        disabled={currentStep === 0}
      >
        Back
      </Button>

      <div class="dots-indicator">
        {#each steps as _, index}
          <button
            class="dot"
            class:active={index === currentStep}
            onclick={() => (currentStep = index)}
            type="button"
            aria-label={`Go to step ${index + 1}`}
          ></button>
        {/each}
      </div>

      <Button variant="primary" onclick={handleNext}>
        {isLastStep ? 'Get Started' : 'Next'}
      </Button>
    </div>
  </div>
</Modal>

<style>
  .wizard-container {
    padding: var(--spacing-xl) var(--spacing-lg);
    max-width: 700px;
    margin: 0 auto;
  }

  .wizard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-lg);
    margin-bottom: var(--spacing-2xl);
  }

  .progress-section {
    flex: 1;
  }

  .step-indicator {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
    margin-bottom: var(--spacing-sm);
  }

  .skip-button {
    background: none;
    border: none;
    color: var(--color-text-tertiary);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    padding: var(--spacing-sm) var(--spacing-md);
    border-radius: var(--radius-md);
    transition: all var(--transition-fast);
  }

  .skip-button:hover {
    color: var(--color-text-primary);
    background-color: var(--color-background-secondary);
  }

  .wizard-content {
    text-align: center;
    margin-bottom: var(--spacing-2xl);
  }

  .illustration-container {
    display: flex;
    justify-content: center;
    margin-bottom: var(--spacing-lg);
  }

  .illustration-icon {
    width: 64px;
    height: 64px;
    color: var(--color-primary);
    opacity: 0.9;
  }

  .step-title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
    margin: 0 0 var(--spacing-sm) 0;
  }

  .step-heading {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-secondary);
    margin: 0 0 var(--spacing-md) 0;
  }

  .step-description {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
    margin: 0 0 var(--spacing-lg) 0;
  }

  .tips-list {
    list-style: none;
    padding: 0;
    margin: 0;
    text-align: left;
    background-color: var(--color-background-secondary);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
  }

  .tip-item {
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
    padding-left: var(--spacing-lg);
    position: relative;
    margin-bottom: var(--spacing-sm);
  }

  .tip-item:last-child {
    margin-bottom: 0;
  }

  .tip-item::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--color-success);
    font-weight: var(--font-weight-bold);
  }

  .wizard-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-md);
  }

  .dots-indicator {
    display: flex;
    gap: var(--spacing-sm);
    align-items: center;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: var(--radius-full);
    background-color: var(--color-border-medium);
    border: none;
    cursor: pointer;
    padding: 0;
    transition: all var(--transition-fast);
  }

  .dot:hover {
    background-color: var(--color-primary);
    opacity: 0.6;
  }

  .dot.active {
    background-color: var(--color-primary);
    width: 24px;
  }

  @media (max-width: 768px) {
    .wizard-container {
      padding: var(--spacing-lg) var(--spacing-md);
    }

    .wizard-header {
      flex-direction: column;
      align-items: stretch;
    }

    .skip-button {
      align-self: flex-end;
    }

    .illustration-icon {
      width: 48px;
      height: 48px;
    }

    .wizard-actions {
      flex-wrap: wrap;
    }

    .dots-indicator {
      order: -1;
      width: 100%;
      justify-content: center;
      margin-bottom: var(--spacing-md);
    }
  }
</style>
